import React, { useState, useEffect, useRef } from 'react';
import { useSnackbar } from 'notistack';
import { Alert, Box, Button, Divider, CircularProgress, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { firstDayofYear, lastDayofYear } from '../../utils/calcDatetime';
import * as yup from 'yup';
import dayjs from 'dayjs';
import * as xlsx from 'xlsx';
import * as projectApi from '../../lib/api/project';
import { SearchBox } from '../../components/forecastmonthstat/SearchBox';
import { forecastStatuses } from '../../utils/forecastMonthStaticData';
import ForecastMonthStateTable from '../../components/forecastmonthstat/ForecastMonthStateTable';

const ForecastMonthStatContainer = () => {
  const tableRef = useRef();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [searchPrjTyp, setSearchPrjTyp] = useState('all');
  const [searchStrtDt, setSearchStrtDt] = useState(firstDayofYear);
  const [searchEndDt, setSearchEndDt] = useState(lastDayofYear);

  const handleDownExcel = () => {
    const el = tableRef;
    const name = '포캐스팅 통계';
    //console.log('handleDownload click')
    htmlToExcel(el.current, name);
  };

  const htmlToExcel = (tableEl, name) => {
    const wb = xlsx.utils.table_to_book(tableEl, {
      sheet: 'sheet1',
      raw: true,
    });
    xlsx.writeFile(wb, name + '.xlsx');
  };

  //   useEffect(() => {
  //     searchList(formik.values);
  //   }, []);

  const searchList = async (values) => {
    setLoading(true);
    const startDate =
      values.searchStrtDt !== null && values.searchStrtDt !== ''
        ? dayjs(values.searchStrtDt).format('YYYY-MM-DD')
        : '';
    const endDate =
      values.searchEndDt !== null && values.searchEndDt !== ''
        ? dayjs(values.searchEndDt).format('YYYY-MM-DD')
        : '';

    let searchData = {
      prjTyp: values.searchPrjTyp,
      strtDt: startDate,
      endDt: endDate,
    };

    setSearchStrtDt(values.searchStrtDt);
    setSearchEndDt(values.searchEndDt);

    //try~catch async
    // const tempData = makeDataForecastMonth();
    // setData(tempData);
    try {
      const res = await projectApi.getProjectsMonthSummary(searchData);
      if (res.data) {
        setData(res.data.list);
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }

    setLoading(false);
  };

  const initialValues = {
    searchPrjTyp: searchPrjTyp,
    searchStrtDt: searchStrtDt,
    searchEndDt: searchEndDt,
  };

  const validationSchema = yup.object({
    searchStrtDt: yup.date().nullable().default(undefined),
    searchEndDt: yup
      .date()
      .min(yup.ref('searchStrtDt'), '검색 종료일을 확인하세요')
      .when('searchStrtDt', (searchStrtDt, schema) => {
        if (searchStrtDt) return schema.required('검색 종료일을 입력하세요');
      })
      .nullable()
      .default(undefined),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);

      setTimeout(() => {
        searchList(values);
        setSubmitting(false);
      }, 1000);
    },
  });

  useEffect(() => {
    if (formik.values?.searchPrjTyp) {
      searchList(formik.values);
    }
  }, [formik?.values?.searchPrjTyp]);

  // 등록 = 수주+진행+실패 검증(같은 행 집합을 상태로만 나눈 것이라 서버에서 항상 일치해야
  // 함) - 합계 행으로 확인하고, 어긋나면 화면에 바로 보이게 경고한다.
  const totalRow = Array.isArray(data) ? data.find((hq) => hq?.id === 'total') : null;
  const totalCell = totalRow?.children?.[0];
  const regCount = Number(totalCell?.REG?.count ?? 0);
  const splitCount =
    Number(totalCell?.WON?.count ?? 0) +
    Number(totalCell?.PROGRESS?.count ?? 0) +
    Number(totalCell?.FAIL?.count ?? 0);
  const countMismatch = Boolean(totalCell) && regCount !== splitCount;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', my: 1 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          gap: 1,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <SearchBox formik={formik} />
        </Box>
        <Button
          type="button"
          color="success"
          variant="contained"
          onClick={handleDownExcel}
          sx={{ mx: 1, whiteSpace: 'nowrap' }}
        >
          엑셀 다운로드
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mx: 1 }}>
        금액은 선택한 기간(매출 시작일~종료일) 내 기회의 매출액 합계입니다. 등록 = 수주 + 진행 +
        실패.
      </Typography>
      {countMismatch && (
        <Alert severity="warning" sx={{ mx: 1, mt: 1 }}>
          등록 건수({regCount}건)와 수주+진행+실패 합({splitCount}건)이 일치하지 않습니다.
        </Alert>
      )}
      <Divider sx={{ my: 1 }} />

      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        {loading ? (
          <Box sx={{ display: 'flex' }}>
            <CircularProgress />
          </Box>
        ) : Array.isArray(data) && data.length === 0 ? (
          <Box>
            <Typography variant="subtitle2">데이터가 없습니다.</Typography>
          </Box>
        ) : (
          <ForecastMonthStateTable
            headerRows={forecastStatuses}
            tableRef={tableRef}
            data={data}
          />
        )}
      </Box>
    </Box>
  );
};

export default ForecastMonthStatContainer;
